import { Box, Button, ClickAwayListener, IconButton, Popper, Stack, TextField, Typography } from "@mui/material";
import { error } from "console";
import { useEffect, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface propsType {
  error: ErrorItem;
  onApplyCorrection: (className: string, newText: string) => void;
}

interface ErrorItem {
  id: string;
  errorContent: string;
  errorType: string;
  replacement?: string[];
  helpText?: string;
}

interface EditPopperProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  original: string;
  color: string;
  value: string;
  setValue: (v: string) => void;
  handleSubmit: () => void;
}

const getErrorColor = (errorType: string): string => {
  switch (errorType) {
    case "어법 오류":
      return "#e53935";
    case "문맥상 오류":
      return "#4caf50";
    default:
      return "#495EFF";
  }
};

const ErrorTag = ({ color }: { color: string }) => (
  <Box sx={{
    width: 8, height: 8, borderRadius: '50%', backgroundColor: color, display: 'inline-block', mr: 1
  }} />
);

const EditPopper = ({ anchorEl, open, onClose, original, color, value, setValue, handleSubmit }: EditPopperProps) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box
          sx={{
            width: 300,
            bgcolor: 'background.paper',
            borderRadius: '15px',
            boxShadow: 3,
            border: '1px solid rgb(219, 219, 219)',
            p: 2,
            mt: 1,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box display='flex' alignItems='center'>
              <Box
                sx={{
                  width: 21,
                  height: 21,
                  borderRadius: '50%',
                  backgroundColor: '#292933',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.6
                }}
              >
                <EditIcon 
                  sx={{
                    width: '0.9rem',
                    height: '0.9rem',
                    color: 'white'
                  }}
                />
              </Box>
              <Typography fontWeight="600" fontSize="1.3rem">대치어 직접 수정하기</Typography>
            </Box>
            <CloseIcon fontSize="medium" onClick={onClose} sx={{ cursor: 'pointer', color: '#A0A0A0' }} />
          </Box>
          
          <Stack direction="column" alignItems="center" sx={{ mt: 1.5, mb: 1.5 }}>
            <Stack direction="row" alignItems="center">
              <ErrorTag color={color} />
              <Typography fontWeight="400" fontSize="1rem" color={color}>{original}</Typography>
            </Stack>
            <KeyboardArrowDownIcon sx={{ width: '18px', height: '18px', color: '#A0A0A0' }} />
          </Stack>

          <TextField
            fullWidth
            placeholder="직접 수정해 보세요!"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            InputProps={{
              sx: {
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '0.95rem',
                paddingY: '4px', // 외곽 box padding
                '& input': {
                  padding: '6px 8px', // 내부 input padding 조정
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E9E9E9',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E9E9E9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E9E9E9',
                },
                '& input::placeholder': {
                  color: '#c0c0c0',
                  opacity: 1,
                },
                '& input:focus::placeholder': {
                  color: '#c0c0c0',
                },
              },
            }}
          />

          <Stack direction={'row'} justifyContent={'center'} spacing={1} mt={1.5}>
            <Button fullWidth variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#5d88f1", height: '41px', borderRadius: '8px' }}>수정하기</Button>
          </Stack>
        </Box>
      </ClickAwayListener>
    </Popper>
  )
};

const IssueBlock = (props: propsType) => {
  const [selectedText, setSelectedText] = useState<string | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const handleOpenModal = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
    setValue('');
  };

  const handleSelect = (className: string, newText: string) => {
    props.onApplyCorrection(className, newText);
    setSelectedText(newText);
  };

  const handleReset = (className: string) => {
    props.onApplyCorrection(className, props.error.errorContent);
    setSelectedText(null);
  }

  return (
    <>
      <Stack spacing={1}>
        <Box display={"flex"} alignItems={"center"}>
          <Box sx={{ width: 75 /* 고정 너비 */ }}>
            <Typography fontWeight="600" fontSize="1rem">
              입력 내용
            </Typography>
          </Box>
            {/* <ErrorTag color={getErrorColor(props.error.errorType)} /> */}
            <Typography 
              fontWeight="700" 
              fontSize="1.1rem" 
              sx={{
                wordBreak: 'break-word', // 단어 기준 줄바꿈
                overflowWrap: 'break-word',
                flex: 1
              }}
            >
              {props.error.errorContent}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center">
            <Box sx={{ width: 66 /* 위와 동일한 고정 너비 */ }}>
              <Typography fontWeight="600" fontSize="1rem">
                대치어
              </Typography>
            </Box>

            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.6
              }}
              onClick={(e) => handleOpenModal(e)}
            >
              <EditIcon 
                sx={{
                  width: '1rem',
                  height: '1rem'
                }}
              />
            </Box>

            <Typography
              color="text.secondary"
              sx={{ cursor: 'pointer', '&:hover': { color: 'black' } }}
              onClick={(e) => handleOpenModal(e)}
            >
              대치어 직접 수정하기
            </Typography>
          </Box>

          <EditPopper
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            original={props.error.errorContent}
            color={getErrorColor(props.error.errorType)}
            value={value}
            setValue={setValue}
            handleSubmit={() => {
              // 수정 처리 로직
              props.onApplyCorrection(props.error.id, value);
              handleClose();
            }}
          />

          <Box display="flex" alignItems="center">
            <Box sx={{ width: 65 /* 위와 동일한 고정 너비 */ }} />
            <Box sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 2, flex: 1, border: '1px solid rgb(233, 233, 233)' }}>
              {props.error.replacement?.map((text, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    m: 1.2
                  }}
                >
                  <Typography
                    sx={{
                      color: '#5D88F1',
                      backgroundColor: '#5D88F126',
                      fontWeight: 700,
                      fontSize: '12px',
                      width: '46px',
                      height: '20px',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: '8px',
                    }}
                  >
                    {"추천 " + (idx + 1)}
                  </Typography>
                  <button
                    onClick={() => handleSelect(props.error.id, text)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: selectedText === text ? '#0027ff' : '#292933',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: selectedText === text ? '700' : '500',
                      padding: 0,
                      textAlign: 'left',
                    }}
                  >
                    {text}
                  </button>
          
                  {selectedText === text && (
                    <IconButton
                      sx={{ width: 19, height: 19, ml: 0.5 }}
                      onClick={() => handleReset(props.error.id)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
          {/* {props.error.helpText && (
            <Box display="flex" alignItems="center">
              <Box sx={{ width: 65 , flexShrink: 0 }}>
                <Typography
                  fontWeight="600"
                  fontSize="1rem"
                >
                  도움말
                </Typography>
              </Box>
              <Typography>{props.error.helpText}</Typography>
            </Box>
          )} */}
      </Stack>
    </>
  )
}

export default IssueBlock;